import React, { createContext, useState, useContext, useEffect } from 'react';
import * as apiService from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  is_admin: boolean;
  branch?: string;
  department?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, adminCode?: string) => Promise<{ requiresVerification?: boolean; userId?: string }>;
  verifyEmail: (userId: string, verificationCode: string) => Promise<void>;
  resendVerificationCode: (userId: string) => Promise<void>;
  updateUser: (updatedUser: Partial<User>) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getTokenExpiry(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return Date.now() + 24 * 60 * 60 * 1000;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[AuthContext] Initializing authentication...');
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      console.log('[AuthContext] Stored user:', storedUser ? 'exists' : 'not found');
      console.log('[AuthContext] Token:', token ? 'exists' : 'not found');
      
      if (storedUser && token) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (!parsedUser.id || !parsedUser.email || !parsedUser.name) {
            throw new Error('Invalid user data');
          }
          
          console.log('[AuthContext] Validating token...');
          // Validate token by making a simple API call
          try {
            // Try to fetch user stats to validate token
            await apiService.getUserStats(parsedUser.id);
            console.log('[AuthContext] Token validation successful, setting user');
            setUser(parsedUser);
          } catch (error: any) {
            console.error('Token validation failed:', error);
            // If token is invalid, clear storage
            localStorage.removeItem('user');
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Error parsing stored user:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      } else {
        console.log('[AuthContext] No stored user or token found');
      }
      console.log('[AuthContext] Authentication initialization complete');
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('Login attempt:', { email }); // Don't log password
      const response = await apiService.login(email, password);
      const { token, user: userData } = response.data;
      
      if (!userData || !userData.id || !userData.email || !userData.name) {
        throw new Error('Invalid user data received from server');
      }

      // Ensure we're using the name from the server response
      const userObj = {
        id: userData.id,
        name: userData.name, // Use the name from the server response
        email: userData.email,
        is_admin: userData.is_admin,
        branch: userData.branch,
        department: userData.department
      };

      const expiresAt = getTokenExpiry(token);
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userObj));
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      
      setUser(userObj);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.message || 'Failed to login');
    }
  };

  const register = async (name: string, email: string, password: string, adminCode?: string) => {
    try {
      const response = await apiService.register(name, email, password, adminCode);
      
      // Check if email verification is required
      if (response.data.requiresVerification) {
        return {
          requiresVerification: true,
          userId: response.data.userId
        };
      }
      
      // Old flow - direct login after registration (if email verification not required)
      const { token, user: userData } = response.data;
      
      const userObj = {
        id: userData.id,
        name: name,
        email: userData.email,
        is_admin: userData.is_admin,
        branch: userData.branch,
        department: userData.department
      };

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userObj));
      setUser(userObj);
      
      return {};
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.response?.data?.message || 'Failed to register');
    }
  };

  const verifyEmail = async (userId: string, verificationCode: string) => {
    try {
      const response = await apiService.verifyEmail(userId, verificationCode);
      const { token, user: userData } = response.data;
      
      const userObj = {
        id: userData.id,
        name: userData.name,
        email: userData.email,
        is_admin: userData.is_admin,
        branch: userData.branch,
        department: userData.department
      };

      const expiresAt = getTokenExpiry(token);

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userObj));
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      setUser(userObj);
    } catch (error: any) {
      throw error;
    }
  };

  const resendVerificationCode = async (userId: string) => {
    try {
      await apiService.resendVerificationCode(userId);
    } catch (error: any) {
      console.error('Resend verification error:', error);
      throw error;
    }
  };

  const updateUser = (updatedUser: Partial<User>) => {
    if (user) {
      const newUser = { ...user, ...updatedUser };
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiresAt');
    setUser(null);
    setIsLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      register,
      verifyEmail,
      resendVerificationCode,
      updateUser,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};